/**
 * Where each dated ballot in a cycle stands, from its date, its polls-close
 * time and the public per-ballot statuses. Shared by the server (which ballot
 * the Tool opens on) and the browser (the Election header), so the two never
 * disagree about whether a ballot is still ahead, being counted, or decided.
 */

import { resultKind } from "./candidacy-status";

const CLOCK = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/Chicago",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

/** Chicago wall-clock date ("YYYY-MM-DD") and time ("HH:MM"). */
export interface ChicagoClock {
  date: string;
  time: string;
}

export function chicagoClock(now = new Date()): ChicagoClock {
  const parts = CLOCK.formatToParts(now);
  const part = (kind: string) => parts.find((p) => p.type === kind)!.value;
  return {
    date: `${part("year")}-${part("month")}-${part("day")}`,
    time: `${part("hour")}:${part("minute")}`,
  };
}

export interface ProgressBallot {
  slug: string;
  date: string;
  stage: string;
  /** "HH:MM", Chicago time. */
  pollsClose: string;
}

/** The slice of a public Race this needs; `PublicCycleRace` satisfies it. */
export interface ProgressRace {
  candidacies: {
    ballots: { ballot: string; status: string | null }[];
  }[];
}

/**
 * - `upcoming`: polls have not opened yet (a later day).
 * - `today`: election day, before polls close.
 * - `pending`: polls closed, at least one Race still has no result.
 * - `decided`: every public Race on the ballot has a result.
 */
export type BallotPhase = "upcoming" | "today" | "pending" | "decided";

export interface BallotProgress {
  slug: string;
  phase: BallotPhase;
  /**
   * A runoff no candidate has advanced into yet. Until someone is marked
   * Advanced on an earlier ballot, a runoff is never guaranteed.
   */
  possible: boolean;
  /** A possible runoff whose source ballot is decided without anyone advancing. */
  notNeeded: boolean;
  /** Races with public candidacies on this ballot: the ones results apply to. */
  races: number;
  /** Races with a result on this ballot. */
  called: number;
  /** Races with a `won` candidate on this ballot. */
  wonOutright: number;
  /** Races with an `advanced` candidate on this ballot. */
  advanced: number;
}


export function pollsClosed(ballot: ProgressBallot, clock: ChicagoClock): boolean {
  return ballot.date < clock.date || (ballot.date === clock.date && clock.time >= ballot.pollsClose);
}

/**
 * A Race is called on a ballot once any candidate there is `won` or
 * `advanced`. Backbone holds only the Races BSC tracks, but each tracked Race
 * holds every candidate on its ballot. Calling stays deliberately simple: one
 * recorded result calls the Race publicly, even before every Seat or party
 * nominee is entered.
 */
function raceResult(race: ProgressRace, slug: string) {
  const runs = race.candidacies.flatMap((c) => {
    const appearance = c.ballots.find((b) => b.ballot === slug);
    return appearance ? [appearance.status] : [];
  });
  if (!runs.some((status) => status !== "withdrawn")) return null;
  return {
    called: runs.some((status) => {
      const kind = resultKind(status);
      return kind === "won" || kind === "advanced";
    }),
    won: runs.some((status) => resultKind(status) === "won"),
    advanced: runs.some((status) => resultKind(status) === "advanced"),
  };
}

export function ballotProgress(
  ballots: readonly ProgressBallot[],
  races: readonly ProgressRace[],
  now = new Date(),
): BallotProgress[] {
  const clock = chicagoClock(now);
  const sorted = [...ballots].sort((a, b) => a.date.localeCompare(b.date));
  const out = sorted.map((b): BallotProgress => {
    const results = races.map((r) => raceResult(r, b.slug)).filter((r) => r !== null);
    const called = results.filter((r) => r.called).length;
    const phase: BallotPhase = !pollsClosed(b, clock)
      ? b.date === clock.date ? "today" : "upcoming"
      : results.length > 0 && called === results.length ? "decided" : "pending";
    return {
      slug: b.slug,
      phase,
      possible: false,
      notNeeded: false,
      races: results.length,
      called,
      wonOutright: results.filter((r) => r.won).length,
      advanced: results.filter((r) => r.advanced).length,
    };
  });
  sorted.forEach((b, i) => {
    if (b.stage !== "runoff") return;
    const earlier = out.slice(0, i);
    if (earlier.length === 0 || earlier.some((p) => p.advanced > 0)) return;
    out[i].possible = true;
    out[i].notNeeded = earlier.at(-1)!.phase === "decided";
  });
  return out;
}

/**
 * The ballot the Tool opens on: the next one whose polls are still open, but
 * a just-held ballot keeps the page while its results are pending, and a
 * runoff nobody has advanced into is never chosen.
 */
export function selectLiveBallot(
  ballots: readonly ProgressBallot[],
  races: readonly ProgressRace[],
  now = new Date(),
): string | null {
  const progress = ballotProgress(ballots, races, now);
  const real = progress.filter((p) => !p.possible);
  const list = real.length > 0 ? real : progress;
  if (list.length === 0) return null;
  const open = list.findIndex((p) => p.phase === "upcoming" || p.phase === "today");
  if (open === -1) return list.at(-1)!.slug;
  const before = list[open - 1];
  return before && before.phase === "pending" && before.races > 0 ? before.slug : list[open].slug;
}
